import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Box,
  Typography,
  Switch,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
} from '@mui/material';
import { PNG } from 'components/Asset/PNG';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { ThemePalette } from 'mui.theme';
import {
  extractReason,
  getTireName,
  getTireNameFromCode,
  getDynamicAssetTireInfo,
} from 'views/TirePressure/util';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useAppSelector } from 'store/hook';
import useAppTheme from 'hooks/useAppTheme';
import { StyledTableHeaderCell } from '../NotificationTable.styles';
import { addLiveDataToAssets } from 'views/FleetOverview/helpers/asset.helpers';
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import { getImageName } from 'utils/helpers/mapAssetImage';
import type { MakeModelDetails } from 'types/make-model.types';
import type {
  TirePattern,
  TirePatternPosition,
} from 'types/tire-pattern.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const NotificationsTable = ({
  data,
  suspendNotification,
  makeModelDetails,
  tirePatterns,
  customerId,
}: {
  data: any[];
  suspendNotification: any;
  makeModelDetails: MakeModelDetails[];
  tirePatterns: {
    tirePatterns: TirePattern[];
    tirePatternPositions: TirePatternPosition[];
  };
  customerId: string;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [open, setOpen] = React.useState<any>(null);
  const [userNote, setUserNote] = React.useState('');
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const navigate = useNavigate();
  const fontColor = React.useMemo(() => palette.textPrimary, [enableDarkTheme]);
  const assetImageBackground =
    CLIENT_THEME === 'yokohama'
      ? 'transparent'
      : enableDarkTheme
      ? '#1C3459'
      : '#F3F4F6';

  const { latestTagData } = useAppSelector((state) => state.deviceReducer);
  const { assets } = useAppSelector((state) => state.assetReducer);
  const assetsWithLiveData = addLiveDataToAssets(assets, latestTagData);

  const getStatusColor = (status: boolean) => {
    return status ? 'error' : 'success';
  };

  const handleClickOpen = (arg: any) => {
    setOpen(arg);
  };

  const handleClose = (isTrue: boolean, note?: string) => {
    setOpen(null);
    if (isTrue) {
      suspendNotification(open, note);
    }
    setUserNote('');
  };

  return (
    <Box sx={{ width: '100%', marginTop: '10px' }}>
      <Dialog
        open={open}
        keepMounted
        onClose={() => {
          handleClose(false);
        }}
        aria-describedby="alert-dialog-slide-description"
        PaperProps={{
          sx: {
            backgroundColor: palette.modalBackground,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: fontColor,
          }}
        >
          {t('are_you_sure_you_want_to_suspend_this_notification')}
        </DialogTitle>
        <DialogContent>
          {open && (
            <div
              style={{ fontSize: '0.9rem', lineHeight: 1.6, color: fontColor }}
            >
              <div
                style={{
                  marginBottom: '5px',
                }}
              >
                <strong>{t('asset')}</strong> {open.asset.bumperNumber}
              </div>
              <div
                style={{
                  marginBottom: '5px',
                }}
              >
                <strong>{t('issue')}</strong>{' '}
                {open.source === 'leakage'
                  ? open.leakType?.toUpperCase().split('_').join(' ') ??
                    t('leak')
                  : extractReason(open?.rule?.ruleName).toUpperCase()}
              </div>
              <div
                style={{
                  marginBottom: '5px',
                }}
              >
                <strong>{t('detected_on')}</strong>
                {convertUtcToDisplayTime(
                  open.alertStartTs,
                  preferredTimeDisplayMode,
                  timezone,
                  DATE_TIME_FORMAT
                )}
              </div>
              <div
                style={{
                  marginTop: '5px',
                  marginBottom: '5px',
                }}
              >
                <TextField
                  label={t('add_a_note_optional')}
                  multiline
                  minRows={3}
                  fullWidth
                  value={userNote}
                  onChange={(e) => {
                    setUserNote(e.target.value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: enableDarkTheme
                        ? ThemePalette?.dark?.cartesianGrid
                        : ThemePalette?.light?.menuOptions,
                      color: enableDarkTheme
                        ? ThemePalette?.typography?.white
                        : ThemePalette?.typography?.black,
                      '& fieldset': {
                        borderColor: enableDarkTheme ? '#2F445D' : '#E0E0E0',
                      },
                      '&:hover fieldset': {
                        borderColor: enableDarkTheme ? '#4A5F7A' : '#B0B0B0',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: enableDarkTheme
                          ? ThemePalette?.typography?.cornflowerBlue
                          : ThemePalette?.typography?.primary,
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: enableDarkTheme
                        ? ThemePalette?.typography?.white
                        : ThemePalette?.typography?.black,
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: enableDarkTheme
                        ? ThemePalette?.typography?.lightGrey
                        : ThemePalette?.typography?.secondary,
                      opacity: 1,
                    },
                  }}
                />
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              backgroundColor: '#EB5757',
              color: '#FFFFFF',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#FFFFFF',
              },
              paddingLeft: '20px',
              paddingRight: '20px',
              marginRight: '10px',
            }}
            onClick={() => {
              handleClose(false);
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            sx={{
              backgroundColor: '#009161',
              color: '#FFFFFF',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#FFFFFF',
              },
              paddingLeft: '20px',
              paddingRight: '20px',
            }}
            onClick={() => {
              handleClose(true, userNote);
            }}
          >
            {t('suspend')}
          </Button>
        </DialogActions>
      </Dialog>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                {t('asset')}
              </StyledTableHeaderCell>
              <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                {t('date')}
              </StyledTableHeaderCell>
              <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                {t('time_word')}
              </StyledTableHeaderCell>
              <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                {t('notification_type')}
              </StyledTableHeaderCell>
              <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                {t('tire_column')}
              </StyledTableHeaderCell>
              <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                {t('status')}
              </StyledTableHeaderCell>{' '}
              <StyledTableHeaderCell
                align="center"
                enabledarktheme={enableDarkTheme}
              >
                {t('last_updated_2')}
              </StyledTableHeaderCell>
              <StyledTableHeaderCell
                align="center"
                enabledarktheme={enableDarkTheme}
              >
                {t('active_suspend')}
              </StyledTableHeaderCell>
              <StyledTableHeaderCell
                enabledarktheme={enableDarkTheme}
              ></StyledTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => {
              const assetTireDetails = getDynamicAssetTireInfo(
                makeModelDetails,
                tirePatterns.tirePatternPositions,
                row.asset.make,
                row.asset.model,
                customerId
              );
              const tirePositions = assetTireDetails.tirePositions;
              return (
                <TableRow
                  key={index}
                  sx={{
                    '& td': {
                      border: 'none',
                      borderBottom:
                        index !== data.length - 1
                          ? '1px solid #2F445E'
                          : 'none',
                    },
                  }}
                >
                  <TableCell sx={{ pl: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: assetImageBackground,
                          borderRadius: '83px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PNG
                          name={getImageName(row.asset, 'Small')}
                          alt={t('assetimage')}
                          style={{
                            height: '35px',
                            width: '100%',
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: '700',
                            color: fontColor,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'left',
                            }}
                          >
                            {row.asset.bumperNumber}
                          </Box>
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: fontColor }}>
                          {row.asset.make}-{row.asset.model}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: fontColor,
                      }}
                    >
                      {convertUtcToDisplayTime(
                        row.alertStartTs,
                        preferredTimeDisplayMode,
                        timezone,
                        DATE_FORMAT
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: fontColor,
                      }}
                    >
                      {convertUtcToDisplayTime(
                        row.alertStartTs,
                        preferredTimeDisplayMode,
                        timezone,
                        'HH:mm'
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: fontColor,
                      }}
                    >
                      {row.source === 'leakage'
                        ? row.leakType?.toUpperCase().split('_').join(' ') ??
                          t('leak')
                        : extractReason(row?.rule?.ruleName).toUpperCase()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: fontColor,
                      }}
                    >
                      {row.source === 'leakage'
                        ? getTireNameFromCode(row.tireCode, tirePositions)
                        : getTireName(
                            row.rule?.ruleConditionAssociations[0]?.tagName ??
                              'N/A',
                            tirePositions
                          )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        row.isActive
                          ? row.source === 'leakage'
                            ? t('active')
                            : extractReason(row?.rule?.ruleName) === 'inflate'
                            ? t('info')
                            : t('active')
                          : t('solved')
                      }
                      color={getStatusColor(row.isActive)}
                      sx={{
                        fontWeight: '600',
                        minWidth: '80px',
                        fontSize: '12px',
                        '&.MuiChip-colorError': {
                          bgcolor:
                            row.source !== 'leakage' &&
                            extractReason(row?.rule?.ruleName) === 'inflate'
                              ? '#3666EC'
                              : '#EB5757',
                          color: 'white',
                        },
                        '&.MuiChip-colorSuccess': {
                          bgcolor: '#009161',
                          color: 'white',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: fontColor,
                      }}
                    >
                      {(row.source === 'leakage' ||
                        extractReason(row?.rule?.ruleName) !== 'inflate') &&
                        !row.isActive && (
                          <>
                            {convertUtcToDisplayTime(
                              row.updatedAt,
                              preferredTimeDisplayMode,
                              timezone,
                              'DD MMM YYYY hh:mm A'
                            )}
                          </>
                        )}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {row.isActive &&
                      (row.source === 'leakage' ||
                        extractReason(row?.rule?.ruleName) !== 'inflate') && (
                        <Switch
                          checked={row.isActive}
                          onChange={() => {
                            handleClickOpen(row);
                          }}
                          color="success"
                        />
                      )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => {
                        const alertDate = new Date(row.alertStartTs);
                        const isoString = alertDate.toISOString();

                        // Get tire code from notification
                        let tireCode = '';
                        if (row.source === 'leakage') {
                          tireCode = row.tireCode ?? '';
                        } else {
                          // For TPMS notifications, extract from tagName (format: prefix_deviceId_tireCode_metric)
                          const tagName =
                            row.rule?.ruleConditionAssociations?.[0]?.tagName ??
                            '';
                          const parts = tagName.split('_');
                          tireCode = parts[2] ?? '';
                        }

                        // Determine metric type based on notification
                        // Leakage notifications are pressure-related
                        // TPMS notifications check if rule name contains 'temperature'
                        const isTemperature =
                          row.source !== 'leakage' &&
                          row.rule?.ruleName
                            ?.toLowerCase()
                            .includes('temperature');
                        const metricType = isTemperature
                          ? 'Temperature'
                          : 'Pressure';

                        // Slow leaks can persist for several days — pass the
                        // alert's own end timestamp so Vehicle Insight opens
                        // with a range spanning the full alert, not a fixed
                        // 1-day window that would cut off the trend.
                        const isSlowLeak =
                          row.source === 'leakage' &&
                          row.leakType === 'slow_leak';
                        const endDateParam =
                          isSlowLeak && row.alertEndTs
                            ? `&endDate=${new Date(
                                row.alertEndTs
                              ).toISOString()}`
                            : '';

                        navigate(
                          `/tire-insights/vehicle-insight?asset=${
                            row.asset.bumperNumber
                          }&date=${isoString}&metric=${metricType}${
                            tireCode ? `&tire=${tireCode}` : ''
                          }${endDateParam}`
                        );
                      }}
                      sx={{
                        color: fontColor,
                        padding: 0,
                        fontSize: '12px',
                      }}
                    >
                      <ArrowForwardIosIcon fontSize="inherit" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default NotificationsTable;
